// TranscriptSync Auth Module for Vuex

// Helper function to safely parse JSON from localStorage
const getStoredJSON = (key) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (e) {
    console.error(`Error parsing ${key} from localStorage:`, e);
    return null;
  }
};

export default {
  namespaced: true,
  
  state: () => ({
    token: localStorage.getItem('transcript_sync_token') || null,
    tokenTimestamp: localStorage.getItem('transcript_sync_token_timestamp') ? parseInt(localStorage.getItem('transcript_sync_token_timestamp')) : null,
    user: getStoredJSON('transcript_sync_user'),
    activeLocation: localStorage.getItem('transcript_sync_active_location') || null,
    ghlAccountId: localStorage.getItem('transcript_sync_ghl_account_id') || null,
    ghlAccount: getStoredJSON('transcript_sync_ghl_account'), // Full GHL account details
  }),
  
  getters: {
    isAuthenticated: (state) => !!state.token,
    isTokenValid: (state) => {
      if (!state.token || !state.tokenTimestamp) return false;
      
      const now = Date.now();
      const tokenAge = now - state.tokenTimestamp;
      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
      
      return tokenAge < sevenDaysInMs;
    },
    tokenExpiresIn: (state) => {
      if (!state.tokenTimestamp) return 0;
      
      const now = Date.now();
      const tokenAge = now - state.tokenTimestamp;
      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
      const remaining = sevenDaysInMs - tokenAge;
      
      return Math.max(0, remaining);
    },
    currentUser: (state) => state.user,
    currentLocation: (state) => state.activeLocation,
    ghlAccountId: (state) => state.ghlAccountId,
    ghlAccount: (state) => state.ghlAccount,
  },
  
  mutations: {
    SET_TOKEN(state, token) {
      state.token = token
      if (token) {
        const timestamp = Date.now();
        state.tokenTimestamp = timestamp;
        localStorage.setItem('transcript_sync_token', token)
        localStorage.setItem('transcript_sync_token_timestamp', timestamp.toString())
      } else {
        state.tokenTimestamp = null;
        localStorage.removeItem('transcript_sync_token')
        localStorage.removeItem('transcript_sync_token_timestamp')
      }
    },
    
    SET_USER(state, user) {
      state.user = user
      if (user) {
        localStorage.setItem('transcript_sync_user', JSON.stringify(user))
      } else {
        localStorage.removeItem('transcript_sync_user')
      }
    },
    
    SET_ACTIVE_LOCATION(state, location) {
      state.activeLocation = location
      if (location) {
        localStorage.setItem('transcript_sync_active_location', JSON.stringify(location))
      } else {
        localStorage.removeItem('transcript_sync_active_location')
      }
    },
    
    SET_GHL_ACCOUNT_ID(state, accountId) {
      state.ghlAccountId = accountId
      if (accountId) {
        localStorage.setItem('transcript_sync_ghl_account_id', accountId)
      } else {
        localStorage.removeItem('transcript_sync_ghl_account_id')
      }
    },
    
    SET_GHL_ACCOUNT(state, account) {
      state.ghlAccount = account
      if (account && account.id) {
        state.ghlAccountId = account.id
        localStorage.setItem('transcript_sync_ghl_account', JSON.stringify(account))
        localStorage.setItem('transcript_sync_ghl_account_id', account.id)
      } else {
        localStorage.removeItem('transcript_sync_ghl_account')
      }
    },
    
    LOGOUT(state) {
      state.token = null
      state.tokenTimestamp = null
      state.user = null
      state.activeLocation = null
      state.ghlAccountId = null
      state.ghlAccount = null
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('transcript_sync_')) {
          localStorage.removeItem(key)
        }
      })
    }
  },
  
  actions: {
    setToken({ commit }, token) {
      commit('SET_TOKEN', token)
    },
    
    setUser({ commit }, user) {
      commit('SET_USER', user)
    },
    
    setActiveLocation({ commit }, location) {
      commit('SET_ACTIVE_LOCATION', location)
    },
    
    setGhlAccountId({ commit }, accountId) {
      commit('SET_GHL_ACCOUNT_ID', accountId)
    },
    
    logout({ commit }) {
      commit('LOGOUT')
    },
    
    async login({ commit }, credentials) {
      // Simulate API call for development
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (credentials.email && credentials.password) {
            const mockToken = 'mock-jwt-token-' + Date.now()
            const mockUser = {
              id: 1,
              email: credentials.email,
              name: 'Test User',
              company_id: 'test-company',
              location_id: 'test-location'
            }
            
            commit('SET_TOKEN', mockToken)
            commit('SET_USER', mockUser)
            resolve({ token: mockToken, user: mockUser })
          } else {
            reject(new Error('Invalid credentials'))
          }
        }, 1000)
      })
    }
  }
}